import { state, style, trigger } from "@angular/animations";
import { DOCUMENT } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";

import { StorageService } from "@hermes/app-core";

import { LiveChatService } from "../../services/livechat.service";

const FLASH_DURATION = 8000;
const SHADOW_ON_LIGHT = "drop-shadow(0 0 8px #FFCE9E)";
const SHADOW_OFF = "drop-shadow(0 0 0 #FFFFFF)";
const MARGIN_LIVECHAT_COOKIE = 24;

export const HIGH_POSITION = "high";
export const MEDIUM_POSITION = "medium";
export const LOW_POSITION = "low";
export type Position =
    | typeof HIGH_POSITION
    | typeof MEDIUM_POSITION
    | typeof LOW_POSITION;

@Component({
    selector: "h-livechat-button",
    templateUrl: "./livechat-button.component.html",
    styleUrls: ["./livechat-button.component.scss"],
    animations: [
        trigger("blinking", [
            state(
                "off",
                style({
                    filter: SHADOW_OFF,
                }),
            ),
            state(
                "on",
                style({
                    filter: SHADOW_ON_LIGHT,
                }),
            ),
            state(
                "flashing",
                style({
                    animationName: "flash-animation",
                    animationDuration: "3s",
                    animationIterationCount: "infinite",
                }),
            ),
        ]),
    ],
})
export class LivechatButtonComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    @ViewChild("livechatButton")
    public livechatButton!: ElementRef;

    @Input()
    public pageType!: string;

    @Input()
    public position!: Position;

    public state = "off";
    private subscription: Subscription = new Subscription();

    private resizeObserver?: ResizeObserver;
    private mutationObserver?: MutationObserver;

    private initialLiveChatButtonBottom?: number;

    constructor(
        private detector: ChangeDetectorRef,
        public liveChatService: LiveChatService,
        private renderer: Renderer2,
        private storageService: StorageService,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    public ngOnInit(): void {
        this.liveChatService.loadLiveChat(this.renderer, this.pageType);

        this.subscription.add(
            this.liveChatService.changeDetectionEmitter.subscribe(() => {
                if (
                    this.liveChatService.showChatButton &&
                    this.liveChatService.newMessage
                ) {
                    // If liveChatAlreadyFlashing is true we bypass the animation to only show the orange circle
                    if (
                        this.storageService.getSessionStorageItem(
                            "liveChatAlreadyFlashing",
                        ) === "true"
                    ) {
                        this.state = "on";
                    } else {
                        this.state = "flashing";
                        // We don't want to replay the animation on refresh or changing page
                        this.storageService.setSessionStorageItem(
                            "liveChatAlreadyFlashing",
                            "true",
                        );
                        // Add a timeout to display the animation for only 8 secondes
                        setTimeout(() => {
                            this.state = "on";
                            this.detector.detectChanges();
                        }, FLASH_DURATION);
                    }
                } else {
                    this.state = "off";
                }

                if (this.liveChatService.showChatButton) {
                    this.initLivechatPositioning();
                }

                this.detector.detectChanges();
            }),
        );
    }

    public ngAfterViewInit(): void {
        this.liveChatService.emitChange();
    }

    public ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();

        this.subscription.unsubscribe();
    }

    /**
     * We might need to update liveChat position if cookie banner covers it.
     */
    private initLivechatPositioning() {
        const cookieBanner =
            this.document.querySelector<HTMLElement>(".cookie-banner");

        if (cookieBanner) {
            this.observeCookieBanner(cookieBanner);
            return;
        }
        const cookieBannerContainer = this.document.querySelector(
            "h-banners-top-container",
        );
        if (cookieBannerContainer && this.mutationObserver === undefined) {
            // Cookie banner not already displayed, we watch its apparition.
            this.mutationObserver = new MutationObserver(() => {
                const detectedBanner =
                    this.document.querySelector<HTMLElement>(".cookie-banner");
                if (detectedBanner) {
                    this.mutationObserver?.disconnect();
                    this.observeCookieBanner(detectedBanner);
                }
            });

            this.mutationObserver.observe(cookieBannerContainer, {
                childList: true,
            });
        }
    }

    private observeCookieBanner(cookieBanner: HTMLElement) {
        this.resizeObserver = new ResizeObserver(() =>
            this.updateLivechatPosition(cookieBanner),
        );
        this.resizeObserver.observe(cookieBanner);
    }

    private updateLivechatPosition(cookieBanner: HTMLElement) {
        if (!this.initialLiveChatButtonBottom) {
            this.initialLiveChatButtonBottom =
                Number.parseInt(
                    getComputedStyle(this.livechatButton.nativeElement).bottom,
                    10,
                ) || MARGIN_LIVECHAT_COOKIE;
        }
        const bannerHeight = cookieBanner?.offsetHeight || 0;

        // If cookie banner has gone or is hiding livechat button, we update livechat position.
        if (
            bannerHeight === 0 ||
            this.initialLiveChatButtonBottom <= bannerHeight
        ) {
            const bottomOffset =
                bannerHeight > 0
                    ? bannerHeight + MARGIN_LIVECHAT_COOKIE
                    : this.initialLiveChatButtonBottom;

            this.renderer.setStyle(
                this.livechatButton?.nativeElement,
                "bottom",
                `${bottomOffset}px`,
            );
        }
    }
}
